import React, { useState } from "react";
import axios from "axios";

import CompareSection from "./CompareSection";
import NewPopup from "./NewPopup";
import { Button, ButtonGroup } from "../../../../components/ui";
import { useAlert } from "react-alert";
import { isEmpty } from "lodash";

// Same-origin proxy on the Laravel backend — see useTradeImage.js for
// rationale (production CORS block on duckdns).
const marketplacesFetchUrl = "/api/integrations/daomarketplace";

const MarketPlacesView = ({
  matchItem,
  productId,
  fetchMarketplacesData,
  isNonMatch,
  setIsRemoved,
  isRemoved,
}) => {
  const alert = useAlert();
  const [isLoading, setIsLoading] = useState(false);
  const [activeItem, setActiveItem] = useState(null);
  const [isFetched, setIsFetched] = useState(false);
  const [fetchedMatches, setFetchedMatches] = useState([{}]);
  const [checkData, setCheckData] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [compareLeftFormData, setCompareLeftFormData] = useState({});

  // Each action clears its spinner in `finally`. On failure (a rejected 4xx
  // such as the upstream 424, or a 5xx/network drop the global interceptor
  // resolves as `undefined`) nothing else changes: the row stays, no popup
  // opens, and the interceptor has already told the user. The button is the
  // retry.
  const onApprove = async () => {
    if (!isNonMatch) {
      setIsLoading(true);
      try {
        const response = await axios.post(
          `${marketplacesFetchUrl}/approved_matches`,
          {
            reference_number: matchItem.scraped_data.reference_number,
            potential_matches: matchItem?.potential_matches?.map(
              (item) => item.reference_number
            ),
            approved_product_id: activeItem.product_id,
            approved_reference: activeItem.reference_number,
          }
        );
        if (!response) return;
        setIsRemoved(true);
      } catch (error) {
        console.error("Failed to approve the marketplace match", error);
      } finally {
        setIsLoading(false);
      }
    } else {
      setIsFetching(true);
      try {
        const response = await axios.post(
          `${marketplacesFetchUrl}/products/check`,
          {
            brand: matchItem.scraped_data.brand,
            model: activeItem.model,
            reference_number: activeItem.reference_number,
          }
        );
        if (!response) return;
        setCheckData(response.data);
        setIsOpen(true);
      } catch (error) {
        console.error("Failed to check the marketplace product", error);
      } finally {
        setIsFetching(false);
      }
    }
  };

  const onFetch = async () => {
    setIsFetching(true);
    try {
      const response = await axios.post(`${marketplacesFetchUrl}/fetch_matches`, {
        brand: matchItem.scraped_data.brand,
        model: compareLeftFormData.model,
        reference_number: compareLeftFormData.referenceNumber,
        dial_color: compareLeftFormData.dialColor,
      });
      if (!response) return;
      const { data } = response;

      setFetchedMatches(data.total_matches > 0 ? data.matches : [{}]);
      if (data.total_matches == 0) {
        alert.show("No matches found");
      }
      setIsFetched(true);
    } catch (error) {
      console.error("Failed to fetch marketplace matches", error);
    } finally {
      setIsFetching(false);
    }
  };

  return (
    <div className="">
      {matchItem?.scraped_data && (
        <>
          <CompareSection
            extractedData={{
              ...matchItem.scraped_data,
              reference_image_url: matchItem.scraped_data.images[0],
            }}
            matchedData={matchItem?.potential_matches || fetchedMatches}
            isNonMatch={isNonMatch}
            fetchedMatches={fetchedMatches}
            setActiveItem={setActiveItem}
            confidence={matchItem.match_confidence}
            isFetched={isFetched}
            setIsFetched={setIsFetched}
            setCompareLeftFormData={setCompareLeftFormData}
            isRemoved={isRemoved}
          />
          <ButtonGroup align="center" className="mt-3 mb-3">
            <Button
              variant="primary"
              data-btn="extraction-market-approve"
              onClick={onApprove}
              disabled={isLoading || isEmpty(activeItem)}
            >
              {isLoading ? "Sending..." : "Approve"}
            </Button>
            {isNonMatch && (
              <Button
                variant="secondary"
                data-btn="extraction-market-fetch"
                onClick={onFetch}
                disabled={false}
              >
                {isFetching ? "Fetching..." : isFetched ? "Fetched" : "Fetch"}
              </Button>
            )}
          </ButtonGroup>
        </>
      )}
      <NewPopup
        source="marketplace"
        checkedData={checkData}
        activeItem={activeItem}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        matchItem={matchItem}
        isNonMatch={isNonMatch}
        setIsFetched={setIsFetched}
        fetchNewData={fetchMarketplacesData}
      />
    </div>
  );
};

export default MarketPlacesView;
